import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent } from '../types.js';
import { escapeHtml } from '../format.js';
import {
  ARABIC_LINE,
  badge,
  description,
  lineCount,
  price,
  sellingPrice,
  variantLabel,
  type MenuTemplate,
  type RenderContext,
} from './common.js';
import {
  WN_FAMILY,
  WN_THEME,
  WN_VARIANTS,
  wnHeader,
  wnHeaderCss,
  wnHeaderHeight,
  wnPhoto,
  wnPhotoCss,
  wnRootCss,
  wnRowsCss,
  wnTab,
} from './walnut-columns.js';

/**
 * Walnut, layout C: every dish as a photo card on the wood, its name and price under the
 * picture, in rows under the bright tab headings. For drinks, desserts or sides where the
 * photos sell.
 */

const S = '.mb-t-walnut-cards';
const TITLE_EM = 4.2;
const PER_ROW = { landscape: 5, portrait: 3 };
const PHOTO = { landscape: 12, portrait: 13 };

function cardHeight(board: MenuBoardContent, item: MenuItemContent, portrait: boolean): number {
  const en = board.settings.language !== 'ar';
  const ar = board.settings.language !== 'en';
  const second = en && ar && item.titleAr?.trim() ? 1.1 * 1.3 * ARABIC_LINE : 0;
  const sizes = item.variants.length ? 1.15 * 1.4 : 0;
  const lines = board.settings.showDescriptions
    ? (en || !item.descriptionAr ? lineCount(item.description, 30, 2) : 0) + (ar ? lineCount(item.descriptionAr, 30, 2) * ARABIC_LINE : 0)
    : 0;
  return PHOTO[portrait ? 'portrait' : 'landscape'] + 0.9 + 1.35 * 1.2 + second + sizes + lines * 0.9 * 1.3 + 0.2 + 1;
}

function column(board: MenuBoardContent, portrait: boolean): MenuColumn {
  const perRow = PER_ROW[portrait ? 'portrait' : 'landscape'];
  const header = wnHeaderHeight(board, TITLE_EM) + 1.8;
  const both = board.settings.language === 'both';
  return {
    capacity: (portrait ? 120 : 67.5) - 5 - header,
    blockCost: (section, items, continued) => {
      let rows = 0;
      for (let i = 0; i < items.length; i += perRow) {
        rows += Math.max(...items.slice(i, i + perRow).map((it) => cardHeight(board, it, portrait)));
      }
      const titled = Boolean(section.title?.trim() || section.titleAr?.trim());
      const subtitle = !continued && Boolean(section.subtitle?.trim() || section.subtitleAr?.trim());
      const head = titled ? 1.55 * 1.3 + 0.7 + 1.1 + (both && section.titleAr ? 1.8 : 0) + (subtitle ? 1.6 : 0) : 0;
      return head + rows + 0.6;
    },
  };
}

/** Photo, then the name with its price (or its sizes on a line of their own) and the description. */
function card(ctx: RenderContext, item: MenuItemContent): string {
  const arabicFirst = !ctx.showEn && item.titleAr?.trim();
  const name = arabicFirst
    ? `<span class="wn-name mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr!.trim())}</span>`
    : `<span class="wn-name">${escapeHtml(item.title)}</span>`;
  const second =
    ctx.showEn && ctx.showAr && item.titleAr?.trim()
      ? `<p class="wn-name2 mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr.trim())}</p>`
      : '';
  const badges = badge(ctx, item, 'wn-badge');
  const sizes = item.variants.length
    ? `<p class="wn3-sizes">${item.variants
        .map((v) => `<span class="wn3-size"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <b dir="ltr">${price(ctx, v.price)}</b></span>`)
        .join('')}</p>`
    : '';
  return [
    '<div class="wn3-card">',
    wnPhoto(ctx, item.imageMediaId, 'wn3-photo'),
    `<div class="wn-line">${name}${badges ? `<span class="wn-badges">${badges}</span>` : ''}`,
    item.variants.length ? '' : `<span class="wn3-pricebox">${sellingPrice(ctx, item, 'wn-price')}</span>`,
    '</div>',
    second,
    sizes,
    description(ctx, item, 'wn-desc'),
    '</div>',
  ].join('');
}

function section(ctx: RenderContext, b: MenuBlock): string {
  return `<div class="wn3-sec">${wnTab(ctx, b)}<div class="wn3-cards">${b.items.map((i) => card(ctx, i)).join('')}</div></div>`;
}

export const walnutCards: MenuTemplate = {
  id: 'walnut-cards',
  name: 'Walnut · Photo cards',
  description:
    'Every dish as a photo card on dark wood, its name and orange price under the picture, in rows under bright tab headings. Matches the other Walnut screens.',
  defaultTheme: WN_THEME,
  variants: WN_VARIANTS,
  cuisines: ['drinks', 'cafe', 'burgers'],
  family: { ...WN_FAMILY, layout: 'Photo cards' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    return packColumns(groupBySection(board), [column(board, orientation === 'portrait')]);
  },

  render(ctx) {
    const body = ctx.page.columns.flat().map((b) => section(ctx, b)).join('');
    return `${wnHeader(ctx, 'wn3-header', ctx.orientation === 'portrait' ? 15 : 24)}<div class="wn3-body">${body}</div>`;
  },

  css: `
${wnRootCss(S)}
${S}{padding:2.5em 3em}
${wnRowsCss(S)}
${wnPhotoCss(S)}
${wnHeaderCss(S, 'wn3-header', TITLE_EM)}
${S} .wn3-header{text-align:center;margin-bottom:1.8em}
${S} .wn3-body{flex:1;min-height:0}
${S} .wn3-sec{margin-bottom:.6em}
${S} .wn3-cards{display:flex;flex-wrap:wrap;column-gap:2.5em}
${S} .wn3-card{flex:0 0 calc((100% - 10em) / 5);min-width:0;margin-bottom:1em}
${S}.mb-portrait .wn3-card{flex-basis:calc((100% - 5em) / 3)}
${S} .wn3-photo{height:${PHOTO.landscape}em;margin-bottom:.9em}
${S} .wn3-pricebox{display:flex;align-items:baseline;gap:.4em;flex-shrink:0;white-space:nowrap}
${S} .wn-price-old{font-size:.95em;opacity:.6;white-space:nowrap}
${S} .wn3-sizes{display:flex;flex-wrap:wrap;gap:.1em 1em;font-size:1.15em;line-height:1.4;color:var(--mb-accent)}
${S} .wn3-size{white-space:nowrap}
${S} .wn3-size small{font-size:.75em;color:var(--mb-text);opacity:.8;margin-inline-end:.2em}
${S}.mb-portrait .wn3-photo{height:${PHOTO.portrait}em}
`,
};
